import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Leaf, Download, FileText, Mail, Sparkles, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default fallback messages (used if API fails or no quotes configured)
const DEFAULT_WELLNESS_MESSAGES = [
  "Your daily food choices shape nearly 80% of your long-term health outcomes.",
  "Most lifestyle diseases don't start in hospitals — they start on the plate.",
  "Eating whole foods consistently is one of the strongest predictors of long-term vitality.",
  "What you eat today decides how your body feels, heals, and performs tomorrow.",
  "Balanced meals help stabilize blood sugar, hormones, mood, and energy — all at once.",
  "People who eat mindfully tend to consume fewer calories without trying.",
  "Nutrition impacts every cell — your body rebuilds itself daily using what you eat.",
  "Small food upgrades done consistently outperform extreme diets every time.",
  "Your metabolism responds more to food quality than calorie counting.",
  "A sustainable diet is the one your body can follow for years, not weeks."
];

// Circular Progress Ring Component
const CircularProgress = ({ progress, size = 200, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#E5E0D1"
        strokeWidth={strokeWidth}
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="url(#progressGradient)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        style={{ transition: 'stroke-dashoffset 1s linear' }}
      />
      {/* Gradient definition */}
      <defs>
        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1A4D2E" />
          <stop offset="100%" stopColor="#D2E823" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const COUNTDOWN_SECONDS = 90;

const ThankYou = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get participant data from multiple sources (fallback chain):
  // 1. Navigation state (primary - from questionnaire submit)
  // 2. URL query params (backup - survives page refresh)
  // 3. Session storage (last resort - backup from questionnaire)
  const getParticipantData = () => {
    // Try navigation state first
    if (location.state?.participantId) {
      return {
        participantId: location.state.participantId,
        participantName: location.state.participantName,
        pdfReady: location.state.pdfReady || false
      };
    }
    
    // Try URL query params
    const urlParams = new URLSearchParams(location.search);
    const pidFromUrl = urlParams.get('pid');
    if (pidFromUrl) {
      // Also check session storage for name
      const nameFromStorage = sessionStorage.getItem('lastParticipantName') || '';
      return {
        participantId: pidFromUrl,
        participantName: nameFromStorage,
        pdfReady: false
      };
    }
    
    // Try session storage as last resort
    const pidFromStorage = sessionStorage.getItem('lastParticipantId');
    const nameFromStorage = sessionStorage.getItem('lastParticipantName') || '';
    if (pidFromStorage) {
      return {
        participantId: pidFromStorage,
        participantName: nameFromStorage,
        pdfReady: false
      };
    }
    
    // No data found
    return { participantId: null, participantName: null, pdfReady: false };
  };
  
  const { participantId, participantName, pdfReady } = getParticipantData();
  
  const [isGenerating, setIsGenerating] = useState(!pdfReady && !!participantId);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isPdfReady, setIsPdfReady] = useState(pdfReady || false);
  const [downloadError, setDownloadError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [wellnessMessages, setWellnessMessages] = useState(DEFAULT_WELLNESS_MESSAGES);
  
  // Countdown timer state
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  
  // Email rate limiting state
  const [emailSendCount, setEmailSendCount] = useState(0);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [lastEmailTime, setLastEmailTime] = useState(null);
  const MAX_EMAIL_SENDS = 3;
  const COOLDOWN_SECONDS = 60;

  // Countdown timer effect
  useEffect(() => {
    if (!isGenerating || isPdfReady) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isGenerating, isPdfReady]);

  // Fetch quotes from API
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const response = await axios.get(`${API}/public/quotes`);
        const quotes = response.data.quotes || [];
        if (quotes.length > 0) {
          setWellnessMessages(quotes.map(q => q.text));
        }
      } catch (error) {
        console.error('Error fetching quotes, using defaults:', error);
        // Keep default messages
      }
    };
    
    fetchQuotes();
  }, []);

  // Rotate messages every 7 seconds
  useEffect(() => {
    if (!isGenerating) return;
    
    const interval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % wellnessMessages.length);
    }, 7000);
    
    return () => clearInterval(interval);
  }, [isGenerating, wellnessMessages.length]);

  // Generate diet plan and PDF
  useEffect(() => {
    if (!participantId || isPdfReady) return;
    
    const generatePlan = async () => {
      try {
        // Generate diet plan (this will use fallback if AI times out)
        await axios.post(`${API}/public/participants/${participantId}/generate-plan`, {}, {
          timeout: 120000 // 2 minute timeout
        });
        
        setIsPdfReady(true);
        setIsGenerating(false);
      } catch (error) {
        console.error('Plan generation error:', error);
        // Even if there's an error, the fallback plan should be created
        setIsPdfReady(true);
        setIsGenerating(false);
      }
    };
    
    generatePlan();
  }, [participantId, isPdfReady]);

  // Email cooldown timer
  useEffect(() => {
    if (emailCooldown <= 0) return;
    
    const timer = setInterval(() => {
      setEmailCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [emailCooldown]);

  const handleDownloadPDF = async () => {
    if (!participantId) {
      setDownloadError('Session expired. Please go back to the questionnaire and submit again.');
      // Redirect to questionnaire after 3 seconds
      setTimeout(() => navigate('/questionnaire'), 3000);
      return;
    }
    
    setIsDownloading(true);
    setDownloadError(null);
    
    try {
      const response = await axios.get(`${API}/public/participants/${participantId}/pdf`, {
        responseType: 'blob',
        timeout: 60000
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FitBharat_${(participantName || 'Wellness').replace(/\s+/g, '_')}_Plan.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        setDownloadError('Plan not found. It may still be generating. Please wait and try again.');
      } else {
        setDownloadError('Failed to download PDF. Please try again.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEmailPDF = async () => {
    // Check if max sends reached
    if (emailSendCount >= MAX_EMAIL_SENDS) {
      toast.error(`Maximum ${MAX_EMAIL_SENDS} emails allowed. Please check your inbox or spam folder.`);
      return;
    }
    
    // Check cooldown
    if (emailCooldown > 0) {
      toast.info(`Please wait ${emailCooldown} seconds before sending another email.`);
      return;
    }
    
    if (!participantId) return;
    
    setIsSendingEmail(true);
    try {
      const response = await axios.post(`${API}/public/participants/${participantId}/send-email`);
      
      // Update send count and start cooldown
      const newCount = emailSendCount + 1;
      setEmailSendCount(newCount);
      setLastEmailTime(Date.now());
      
      // Start cooldown if not at max sends
      if (newCount < MAX_EMAIL_SENDS) {
        setEmailCooldown(COOLDOWN_SECONDS);
      }
      
      if (response.data.already_sent) {
        toast.info('Email sent! Please check your inbox or spam folder.');
      } else {
        toast.success(`Email sent successfully! (${newCount}/${MAX_EMAIL_SENDS})`);
      }
      
      // Mark as "sent" only after max sends
      if (newCount >= MAX_EMAIL_SENDS) {
        setEmailSent(true);
      }
    } catch (error) {
      console.error('Email error:', error);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Get email button text
  const getEmailButtonText = () => {
    if (isSendingEmail) return 'Sending...';
    if (emailSendCount >= MAX_EMAIL_SENDS) return `Sent (${MAX_EMAIL_SENDS}/${MAX_EMAIL_SENDS})`;
    if (emailCooldown > 0) return `Wait ${emailCooldown}s`;
    if (emailSendCount > 0) return `Resend (${emailSendCount}/${MAX_EMAIL_SENDS})`;
    return 'Email me a copy';
  };

  // Loading screen with circular countdown timer
  // Issue 5 fix: Check isPdfReady immediately to show success screen
  
  // If no participant ID found, show error and redirect option
  if (!participantId) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-[#F9F8F6] to-white flex items-center justify-center px-4 py-8 md:py-12">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8">
            <AlertCircle className="w-10 h-10 md:w-12 md:h-12 text-red-500" />
          </div>
          
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-3 md:mb-4">
            Session Expired
          </h1>
          
          <p className="text-gray-600 text-base md:text-lg mb-6 md:mb-8 px-2">
            Your session has expired or the page was refreshed. Please complete the questionnaire again to generate your personalized plan.
          </p>
          
          <Button
            onClick={() => navigate('/questionnaire')}
            className="w-full max-w-xs mx-auto bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 text-white rounded-full h-12 md:h-14 text-base md:text-lg font-bold shadow-lg"
          >
            Start Questionnaire
          </Button>
        </div>
      </div>
    );
  }
  
  if (isGenerating && !isPdfReady) {
    const progress = (countdown / COUNTDOWN_SECONDS) * 100;
    const isTimerExpired = countdown === 0;
    const firstName = participantName ? participantName.split(' ')[0] : '';
    
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-[#F9F8F6] to-white flex items-center justify-center px-4 py-8 md:py-12">
        <div className="max-w-lg w-full text-center">
          
          {/* Loading Text with User Name - Issue 2 */}
          <h1 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-4 md:mb-6 px-2">
            Creating Your Personalized Plan{firstName ? `, ${firstName}` : ''}
          </h1>

          {/* Estimated Time Label - Issue 1: Large text above timer */}
          {!isTimerExpired && (
            <p className="text-lg md:text-xl font-semibold text-[#1A4D2E] mb-3 md:mb-4">
              Estimated time remaining
            </p>
          )}
          
          {/* Circular Progress Timer - Issue 1: Single timer only */}
          <div className="relative inline-flex items-center justify-center mb-6 md:mb-8">
            <CircularProgress 
              progress={progress} 
              size={180} 
              strokeWidth={10} 
            />
            {/* Timer number in center */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {isTimerExpired ? (
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Leaf className="w-12 h-12 text-[#1A4D2E]" />
                </motion.div>
              ) : (
                <motion.span 
                  key={countdown}
                  initial={{ scale: 1.2, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-5xl md:text-6xl font-bold text-[#1A4D2E]"
                >
                  {countdown}
                </motion.span>
              )}
            </div>
          </div>

          {/* Status Label - Only show when timer expired */}
          {isTimerExpired && (
            <div className="mb-4 md:mb-6">
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-lg md:text-xl font-medium text-[#1A4D2E]"
              >
                Almost ready...
              </motion.p>
            </div>
          )}
          
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6 md:mb-8">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#D2E823]"
                animate={{ 
                  scale: [1, 1.5, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.3
                }}
              />
            ))}
          </div>

          {/* Rotating Wellness Message */}
          <div className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-lg border border-[#E5E0D1] mb-6 md:mb-8 min-h-[100px] md:min-h-[120px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentMessageIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="flex items-start gap-3 md:gap-4"
              >
                <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-[#D2E823] shrink-0 mt-0.5 md:mt-1" />
                <p className="text-sm md:text-lg text-[#1A4D2E] italic text-left leading-relaxed">
                  &ldquo;{wellnessMessages[currentMessageIndex]}&rdquo;
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Sub-text */}
          <p className="text-gray-500 text-xs md:text-sm px-4">
            Analyzing your profile and creating personalized recommendations...
          </p>
        </div>
      </div>
    );
  }

  // Success screen with download button
  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-[#F9F8F6] to-white flex items-center justify-center px-4 py-8 md:py-12">
      <div className="max-w-lg w-full text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="w-20 h-20 md:w-24 md:h-24 bg-[#D2E823] rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8"
        >
          <CheckCircle2 className="w-10 h-10 md:w-12 md:h-12 text-[#1A4D2E]" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {/* Logo - Issue 3: Replace text with logo */}
          <div className="flex items-center justify-center mb-3 md:mb-4">
            <img 
              src="/FBM_Logo_Dark.png" 
              alt="Fit Bharat Mission" 
              className="h-10 md:h-12 w-auto"
            />
          </div>

          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-3 md:mb-4">
            Your Plan is Ready! 🎉
          </h1>
          
          <p className="text-gray-600 text-base md:text-lg mb-6 md:mb-8 px-2">
            {participantName ? `Congratulations ${participantName.split(' ')[0]}! ` : ''}
            Your personalized 3-month diet and lifestyle plan has been created.
          </p>

          {/* Download Card */}
          <div className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-lg border border-[#E5E0D1] mb-6 md:mb-8">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1A4D2E] rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
              <FileText className="w-7 h-7 md:w-8 md:h-8 text-white" />
            </div>
            
            <h3 className="font-semibold text-[#1A4D2E] text-base md:text-lg mb-1 md:mb-2">
              Your Personalized Wellness Plan
            </h3>
            <p className="text-gray-500 text-xs md:text-sm mb-4 md:mb-6">
              Includes health metrics, daily meal schedules, and expert tips
            </p>

            {downloadError && (
              <p className="text-red-500 text-xs md:text-sm mb-3 md:mb-4">{downloadError}</p>
            )}

            <Button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className="w-full bg-[#D2E823] hover:bg-[#D2E823]/90 text-[#1A4D2E] rounded-full h-12 md:h-14 text-base md:text-lg font-bold shadow-lg active:scale-[0.98] touch-manipulation"
              data-testid="btn-download-pdf"
            >
              {isDownloading ? (
                <>
                  <div className="spinner mr-2" />
                  Preparing Download...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Download Your Plan (PDF)
                </>
              )}
            </Button>

            {/* Email Button */}
            <Button
              onClick={handleEmailPDF}
              disabled={isSendingEmail || emailSendCount >= MAX_EMAIL_SENDS || emailCooldown > 0}
              variant="outline"
              className={`w-full rounded-full h-11 md:h-12 mt-3 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E]/5 touch-manipulation ${
                emailCooldown > 0 ? 'opacity-70' : ''
              }`}
              data-testid="btn-email-pdf"
            >
              {emailSendCount >= MAX_EMAIL_SENDS ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                  Sent ({MAX_EMAIL_SENDS}/{MAX_EMAIL_SENDS})
                </>
              ) : isSendingEmail ? (
                <>
                  <div className="spinner mr-2" />
                  Sending...
                </>
              ) : emailCooldown > 0 ? (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Wait {emailCooldown}s
                </>
              ) : emailSendCount > 0 ? (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Resend ({emailSendCount}/{MAX_EMAIL_SENDS})
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Email me a copy
                </>
              )}
            </Button>
          </div>

          {/* What's included - Issue 4: Improved text */}
          <div className="bg-[#F5F1E8] rounded-xl md:rounded-2xl p-4 md:p-6 mb-6 md:mb-8 text-left">
            <h4 className="font-semibold text-[#1A4D2E] mb-2 md:mb-3 text-sm md:text-base">What&apos;s in your plan:</h4>
            <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D2E823] shrink-0 mt-0.5" />
                Complete health profile with BMI & ideal weight analysis
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D2E823] shrink-0 mt-0.5" />
                Personalized daily calorie & macro targets
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D2E823] shrink-0 mt-0.5" />
                3-month meal plan (12 weeks) with regional Indian recipes
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D2E823] shrink-0 mt-0.5" />
                6 balanced meals/day with portion & calorie guide
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D2E823] shrink-0 mt-0.5" />
                6-Pillar holistic wellness guide
              </li>
            </ul>
          </div>

          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-[#1A4D2E] min-h-[44px] touch-manipulation"
            data-testid="btn-back-home"
          >
            Back to Home
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default ThankYou;
